import { ComponentClassNames } from '../../util/classNames';
import { addAlpha } from '../../util/colors';
import { ITheme, memoizeFunction, mergeStyleSets } from '../../util/styling';
import { defaultCardBorderRadius } from '../Card';

interface GetClassNamesProps {
  readonly imgSrc: string;
  readonly fullWidthLayout: boolean;
  readonly hasHeader: boolean;
  readonly noSpacing: boolean;
  readonly theme: ITheme;
  readonly topOffset: number;
  readonly width: PageLayoutWidth | undefined;
}
import { defaultHeaderHeight, headerHeightSmall } from './PageLayout.styles';
import { PageLayoutWidth } from './PageLayout.types';

const heroBannerTopMarginNarrow = 0;
const heroBannerTopMarginWide = 24;
const mediumWidthMaxValue = 1044;
const largeWidthMaxValue = 1190;

const contentWrapperGradientTopOffsetNarrow = 12;
const contentWrapperGradientTopOffsetWide = 36;
const contentWrapperGradientSideMargin = 36;

const maxContentWrapperGradientWidths: Record<PageLayoutWidth, string> = {
  [PageLayoutWidth.Small]: '100%',
  [PageLayoutWidth.Medium]: `${mediumWidthMaxValue} - ${contentWrapperGradientSideMargin}px`,
  [PageLayoutWidth.Large]: `${largeWidthMaxValue - contentWrapperGradientSideMargin * 2}px`,
  [PageLayoutWidth.XLarge]: `${largeWidthMaxValue - contentWrapperGradientSideMargin * 2}px`,
};

const getMaxContentWrapperGradientWidth = (width: PageLayoutWidth | undefined) => {
  if (width === undefined) {
    return undefined;
  }

  return maxContentWrapperGradientWidths[width];
};

type PageLayoutClassNames = ComponentClassNames<'heroBanner' | 'contentWrapperGradient'>;
export const getClassNames = memoizeFunction(
  ({
    fullWidthLayout,
    hasHeader,
    imgSrc,
    noSpacing,
    theme,
    topOffset,
    width,
  }: GetClassNamesProps): PageLayoutClassNames => {
    const isSmall = width === PageLayoutWidth.Small;
    const isSmallOrMedium = isSmall || width === PageLayoutWidth.Medium;
    const contentWrapperGradientTopOffset = isSmall
      ? contentWrapperGradientTopOffsetNarrow
      : contentWrapperGradientTopOffsetWide;
    const heroBannerTopMargin = isSmall ? heroBannerTopMarginNarrow : heroBannerTopMarginWide;
    const headerHeight = isSmall ? headerHeightSmall : defaultHeaderHeight;
    const heroBannerTopOffset = hasHeader
      ? headerHeight + topOffset + heroBannerTopMargin
      : topOffset + heroBannerTopMargin;
    const heroBannerSideMargin = isSmallOrMedium ? 0 : 24;
    const contentWrapperGradientSideMargin = 36;
    const maxContentWrapperGradientWidth =
      fullWidthLayout || noSpacing ? undefined : getMaxContentWrapperGradientWidth(width);
    const contentColumnWidth = isSmallOrMedium ? '100%' : `calc(100% - ${heroBannerSideMargin}px)`;
    const backgroundGradient = `linear-gradient(to bottom, ${addAlpha(theme.semanticColors.bodyBackground, 0)}, ${
      theme.semanticColors.bodyBackground
    })`;
    const backgroundImage = `url(${imgSrc})`;
    const topBorderRadius = isSmallOrMedium ? 0 : defaultCardBorderRadius;

    return mergeStyleSets({
      heroBanner: {
        position: 'absolute',
        top: heroBannerTopOffset,
        height: 244,
        borderRadius: `${topBorderRadius} ${topBorderRadius} 0 0`,
        width: `calc(100% - ${heroBannerSideMargin * 2}px)`,
        backgroundImage: `${backgroundGradient}, ${backgroundImage}`,
        backgroundPosition: 'top center',
        backgroundSize: 'cover',
      },
      contentWrapperGradient: {
        height: '100%',
        margin: `${contentWrapperGradientTopOffset}px auto 0`,
        padding: `0 ${contentWrapperGradientSideMargin}`,
        width: contentColumnWidth,
        maxWidth: maxContentWrapperGradientWidth,
        borderRadius: `${topBorderRadius} ${topBorderRadius} 0 0`,
        background: `linear-gradient(to bottom, ${addAlpha(theme.palette.white, 90)},${addAlpha(
          theme.semanticColors.bodyBackground,
          10
        )})`,
      },
    });
  }
);
